<script lang="ts">
    import cn from "classnames";

    import { TooltipPosition } from "./types";

    export let position: TooltipPosition = "top";
    export let theme: "dark" | "light" = "dark";
    export let arrow = false;
    export let active = false;
    export let followCursor = false;
    export let maxWidth = "200px";
    export let distance = 8;

    $: tooltipClasses = cn(
        "gw-tooltip-content",
        `gw-tooltip-${position}`,
        `gw-tooltip-${theme}`,
        {
            "gw-tooltip-no-arrow": !arrow,
        }
    );

    $: tooltipStyles = `
        --max-width: ${maxWidth};
        --distance: ${distance}px;
        ${followCursor ? "position: fixed;" : ""}
    `;
</script>

{#if active}
    <div class={tooltipClasses} style={tooltipStyles}>
        <slot />
        {#if arrow}
            <div class="gw-tooltip-arrow {position}" />
        {/if}
    </div>
{/if}

<style lang="postcss">
    .gw-tooltip-content {
        &.gw-tooltip-dark {
            --gw-tooltip-title-color: var(--gw-tooltip-dark-title-color);
            --gw-tooltip-bg: var(--gw-tooltip-dark-bg);
        }
        &.gw-tooltip-light {
            --gw-tooltip-title-color: var(--gw-tooltip-light-title-color);
            --gw-tooltip-bg: var(--gw-tooltip-light-bg);
            box-shadow: var(
                --gw-popover-shadow,
                0px 3px 6px -3px rgba(23, 24, 24, 0.08),
                0px 8px 20px -4px rgba(23, 24, 24, 0.12)
            );
        }

        position: absolute;
        width: max-content;
        max-width: var(--max-width, 200px);
        font-size: var(--gw-tooltip-font-size, 0.875em);
        line-height: normal;
        word-wrap: break-word;
        padding: 4px 8px;
        transition: opacity 200ms cubic-bezier(0.4, 0, 0.2, 1),
            transform 133ms cubic-bezier(0.4, 0, 0.2, 1);
        color: var(--gw-tooltip-title-color, #ffffff);
        background-color: var(--gw-tooltip-bg, rgba(0, 0, 0, 0.75));
        border-radius: 4px;
        box-sizing: border-box;
        z-index: 10;

        &::after {
            content: "";
            position: absolute;
            border-style: solid;
            display: block;
            width: 0;
            height: 0;
            border-width: 6px;
            border-color: transparent;
        }
    }

    .gw-tooltip-no-arrow {
        &::after {
            content: none;
        }
    }

    .gw-tooltip-top {
        bottom: calc(100% + var(--distance, 8px));
        left: 50%;
        transform: translateX(-50%);

        &::after {
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            border-top-color: var(--gw-tooltip-bg, rgba(0, 0, 0, 0.75));
        }
    }

    .gw-tooltip-top-start {
        bottom: calc(100% + var(--distance, 8px));
        left: 0;

        &::after {
            top: 100%;
            left: 10%;
            border-top-color: var(--gw-tooltip-bg);
        }
    }

    .gw-tooltip-top-end {
        bottom: calc(100% + var(--distance, 8px));
        right: 0;

        &::after {
            top: 100%;
            right: 10%;
            border-top-color: var(--gw-tooltip-bg, rgba(0, 0, 0, 0.75));
        }
    }

    .gw-tooltip-right {
        top: 50%;
        left: calc(100% + var(--distance, 8px));
        transform: translateY(-50%);

        &::after {
            left: -6px;
            top: 50%;
            transform: translateY(-50%);
            border-right-color: var(--gw-tooltip-bg, rgba(0, 0, 0, 0.75));
        }
    }

    .gw-tooltip-right-start {
        top: 0;
        left: calc(100% + var(--distance, 8px));

        &::after {
            left: -6px;
            top: 10%;
            border-right-color: var(--gw-tooltip-bg, rgba(0, 0, 0, 0.75));
        }
    }

    .gw-tooltip-right-end {
        bottom: 0;
        left: calc(100% + var(--distance, 8px));

        &::after {
            left: -6px;
            bottom: 10%;
            border-right-color: var(--gw-tooltip-bg, rgba(0, 0, 0, 0.75));
        }
    }

    .gw-tooltip-bottom {
        top: calc(100% + var(--distance, 8px));
        left: 50%;
        transform: translateX(-50%);

        &::after {
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%);
            border-bottom-color: var(--gw-tooltip-bg, rgba(0, 0, 0, 0.75));
        }
    }

    .gw-tooltip-bottom-start {
        top: calc(100% + var(--distance, 8px));
        left: 0;

        &::after {
            bottom: 100%;
            left: 10%;
            border-bottom-color: var(--gw-tooltip-bg, rgba(0, 0, 0, 0.75));
        }
    }

    .gw-tooltip-bottom-end {
        top: calc(100% + var(--distance, 8px));
        right: 0;

        &::after {
            bottom: 100%;
            right: 10%;
            border-bottom-color: var(--gw-tooltip-bg, rgba(0, 0, 0, 0.75));
        }
    }

    .gw-tooltip-left {
        top: 50%;
        right: calc(100% + var(--distance, 8px));
        transform: translateY(-50%);

        &::after {
            right: -6px;
            top: 50%;
            transform: translateY(-50%);
            border-left-color: var(--gw-tooltip-bg, rgba(0, 0, 0, 0.75));
        }
    }

    .gw-tooltip-left-start {
        top: 0;
        right: calc(100% + var(--distance, 8px));

        &::after {
            right: -6px;
            top: 10%;
            border-left-color: var(--gw-tooltip-bg, rgba(0, 0, 0, 0.75));
        }
    }

    .gw-tooltip-left-end {
        bottom: 0;
        right: calc(100% + var(--distance, 8px));

        &::after {
            right: -6px;
            bottom: 10%;
            border-left-color: var(--gw-tooltip-bg, rgba(0, 0, 0, 0.75));
        }
    }
</style>
